import React, { useState } from 'react'
import PropTypes from 'prop-types'
import isEmpty from 'lodash/isEmpty'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { MaterialIcon } from '@dsny/dsny-component-library'
import ArchivedActions from 'reducers/Archived'
import { thunks as DownloadsThunks } from 'reducers/Downloads'
import FlaggedActions, { thunks as FlaggedThunks } from 'reducers/Flagged'
import ModalActions from 'reducers/Modal'
import NotificationActions from 'reducers/Notification'
import PlaylistActions, { thunks as PlaylistThunks } from 'reducers/PlayList'
import ReleaseActions, { thunks as ReleaseThunks } from 'reducers/Releases'
import { NotificationType } from 'constants/enums'
import { ReleasesService } from 'services'
import { normalizeValue } from 'utils/analytics'
import { trackEvent } from 'utils/segmentService'
import useArchiveToFilter from 'utils/hooks/useArchiveToFilter'
import styles from '../Dropdowns.module.scss'

const menuContentClassName = [
  styles.DropdownMenuContent,
  styles.ReleaseInfoActionsDropdown
].join(' ')

const menuItemClassName = [
  styles.DropdownMenuItem,
  styles.ReleaseInfoActionsItem
].join(' ')

const updateFlaggedTracks = (tracks, flagged) => {
  if (!Array.isArray(tracks)) return tracks

  return tracks.map((track) => {
    if (!track || typeof track !== 'object') return track
    return { ...track, flagged }
  })
}

const getReleaseTrackIds = (release) => {
  const tracks = release?.songs || release?.tracks || []

  return tracks
    .filter(Boolean)
    .filter((track) => track?.assettype !== 'video')
    .map((track) => (typeof track === 'object' ? track.trackid : track))
    .filter(Boolean)
}

const ReleaseActionsMenuContent = ({ Menu, release, source }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const { playlistAbstracts } = useSelector((state) => state.playList)
  const { enabled: archiveToFilterEnabled } = useArchiveToFilter()
  const restricted = archiveToFilterEnabled && Boolean(release.archived)
  const archivePending = useSelector((state) =>
    state.releases.pendingArchiveIds?.includes(String(release.releaseid))
  )
  const [isPlaylistSubmenuOpen, setIsPlaylistSubmenuOpen] = useState(false)

  const syncReleaseState = (updatedRelease) => {
    dispatch(ReleaseActions.setCurrentReleaseInformation(updatedRelease))
    dispatch(ReleaseActions.updateRelease(updatedRelease))
  }

  const getDetailedRelease = async () => {
    const releaseDetail = await ReleasesService.getDetail(release.releaseid)

    if (isEmpty(releaseDetail.data)) return null

    return {
      ...release,
      ...releaseDetail.data
    }
  }

  const handleDownload = async (format) => {
    if (restricted) return
    try {
      const detailedRelease = await getDetailedRelease()

      if (detailedRelease && detailedRelease.candownload !== false) {
        dispatch(
          DownloadsThunks.addReleaseToDownloadQueue(
            detailedRelease,
            format,
            source
          )
        )
      }
    } catch (error) {
      console.warn(error)
    }
  }

  const createNewPlaylist = async () => {
    if (restricted) return
    try {
      const detailedRelease = await getDetailedRelease()

      if (!detailedRelease) return

      dispatch(
        PlaylistActions.setSongsToAdd(getReleaseTrackIds(detailedRelease))
      )
      dispatch(ModalActions.openPlaylistModal('release_more_actions'))
    } catch (error) {
      console.warn(error)
    }
  }

  const handleAddToPlaylist = async (playlist) => {
    if (restricted) return
    try {
      const detailedRelease = await getDetailedRelease()

      if (!detailedRelease) return

      const onSuccess = () => {
        dispatch(
          NotificationActions.broadcastMessage(
            {
              songTitle: `${release.artist} - ${release.title}`,
              playlistTitle: playlist.name
            },
            NotificationType.addedToPlaylist
          )
        )
      }

      dispatch(
        PlaylistThunks.addTracks(
          playlist.playlistid,
          getReleaseTrackIds(detailedRelease),
          onSuccess
        )
      )
    } catch (error) {
      console.warn(error)
    }
  }

  const handleFlag = () => {
    const nextFlagged = !release?.flagged

    dispatch(
      FlaggedThunks.flag(
        release.releaseid,
        nextFlagged,
        () => {
          const updatedRelease = {
            ...release,
            flagged: nextFlagged,
            songs: updateFlaggedTracks(release.songs, nextFlagged),
            tracks: updateFlaggedTracks(release.tracks, nextFlagged)
          }

          syncReleaseState(updatedRelease)

          if (nextFlagged) {
            dispatch(FlaggedActions.addFlaggedRelease(updatedRelease))
          } else {
            dispatch(FlaggedActions.removeFlaggedReleaseId(release.releaseid))
          }

          dispatch(
            NotificationActions.broadcastMessage(
              `${release.artist} - ${release.title}`,
              nextFlagged ? NotificationType.flag : NotificationType.unFlag
            )
          )

          trackEvent('Release Flag', {
            release_id: normalizeValue(release.releaseid),
            artist: normalizeValue(release.artist),
            title: normalizeValue(release.title),
            action: nextFlagged ? 'flag' : 'unflag',
            source,
            type: 'release_flag'
          })
        },
        () => {}
      )
    )
  }

  const handleArchive = () => {
    const currentValue = Boolean(release?.archived)
    const currentFlagged = Boolean(release?.flagged)
    const scrollElement =
      typeof document === 'undefined'
        ? null
        : document.getElementById('scrollableDiv')
    const scrollTop = scrollElement?.scrollTop || 0

    dispatch(
      ReleaseThunks.archive(
        release.releaseid,
        !currentValue,
        () => {
          const shouldUnflagOnArchive = !currentValue && currentFlagged
          const updatedRelease = {
            ...release,
            archived: !currentValue,
            flagged: shouldUnflagOnArchive ? false : release.flagged,
            songs: shouldUnflagOnArchive
              ? updateFlaggedTracks(release.songs, false)
              : release.songs,
            tracks: shouldUnflagOnArchive
              ? updateFlaggedTracks(release.tracks, false)
              : release.tracks
          }

          syncReleaseState(updatedRelease)

          if (archiveToFilterEnabled) {
            dispatch(ReleaseActions.removeRelease(updatedRelease))
          } else if (currentValue) {
            dispatch(ReleaseActions.addRelease(updatedRelease))
            dispatch(ArchivedActions.removeArchivedRelease(updatedRelease))
          } else {
            dispatch(ReleaseActions.removeRelease(updatedRelease))
            dispatch(ArchivedActions.addArchivedRelease(updatedRelease))
          }

          dispatch(
            NotificationActions.broadcastMessage(
              `${release.artist} - ${release.title}`,
              currentValue
                ? NotificationType.unArchive
                : NotificationType.archive
            )
          )

          trackEvent('Release Archive', {
            release_id: normalizeValue(release.releaseid),
            artist: normalizeValue(release.artist),
            title: normalizeValue(release.title),
            action: currentValue ? 'unarchive' : 'archive',
            unflagged_on_archive: normalizeValue(shouldUnflagOnArchive),
            source,
            type: 'release_archive'
          })

          if (shouldUnflagOnArchive) {
            dispatch(
              FlaggedThunks.flag(release.releaseid, false, () => {
                dispatch(
                  FlaggedActions.removeFlaggedReleaseId(release.releaseid)
                )
              })
            )
          }

          if (scrollElement && !archiveToFilterEnabled) {
            window.requestAnimationFrame(() => {
              scrollElement.scrollTop = scrollTop
              dispatch(
                ReleaseActions.setReleaseGalleryScrollPosition(scrollTop)
              )
            })
          }
        },
        () => {},
        archiveToFilterEnabled
      )
    )
  }

  return (
    <>
      {release.candownload && !restricted && (
        <>
          <Menu.Item
            className={menuItemClassName}
            data-cy="release-action-download-wav"
            onSelect={() => handleDownload('wav')}
          >
            {t('general.download-all-wav')}
          </Menu.Item>
          <Menu.Item
            className={menuItemClassName}
            data-cy="release-action-download-mp3"
            onSelect={() => handleDownload('mp3')}
          >
            {t('general.download-all-mp3')}
          </Menu.Item>
        </>
      )}

      <Menu.Item
        className={menuItemClassName}
        data-cy="release-action-information"
        onSelect={() => navigate(`/releases/releaseInfo/${release.releaseid}`)}
      >
        {t('general.release-information')}
      </Menu.Item>

      {!restricted && (
        <Menu.Sub
          onOpenChange={setIsPlaylistSubmenuOpen}
          open={isPlaylistSubmenuOpen}
        >
          <Menu.SubTrigger
            className={menuItemClassName}
            onMouseEnter={() => setIsPlaylistSubmenuOpen(true)}
          >
            {t('playlist.add-to-playlist')}
            <div className={styles.RightSlot}>
              <MaterialIcon
                color={theme.colors.neutralW100}
                name="KeyboardArrowRight"
              />
            </div>
          </Menu.SubTrigger>
          <Menu.Portal>
            <Menu.SubContent
              alignOffset={-5}
              avoidCollisions
              className={menuContentClassName}
              collisionPadding={20}
              sideOffset={2}
              style={{
                background: theme.colors.neutralW20,
                color: theme.colors.neutralW100,
                maxHeight: 'min(400px, calc(100vh - 40px))',
                overflowX: 'hidden',
                overflowY: 'auto',
                overscrollBehavior: 'contain'
              }}
            >
              {playlistAbstracts?.map((playlist) => (
                <Menu.Item
                  key={playlist?.playlistid}
                  className={menuItemClassName}
                  onSelect={() => handleAddToPlaylist(playlist)}
                >
                  {playlist?.name}
                </Menu.Item>
              ))}
              <Menu.Item
                className={menuItemClassName}
                onSelect={createNewPlaylist}
              >
                {t('playlist.new-playlist')}
              </Menu.Item>
            </Menu.SubContent>
          </Menu.Portal>
        </Menu.Sub>
      )}

      <Menu.Item
        className={menuItemClassName}
        data-cy="release-action-flag"
        onSelect={handleFlag}
      >
        {release.flagged ? t('general.unflag') : t('general.flag')}
      </Menu.Item>
      <Menu.Item
        className={menuItemClassName}
        data-cy="release-action-archive"
        disabled={archivePending}
        onSelect={handleArchive}
      >
        {release.archived ? t('general.restore') : t('general.archive')}
      </Menu.Item>
    </>
  )
}

ReleaseActionsMenuContent.propTypes = {
  Menu: PropTypes.object.isRequired,
  release: PropTypes.object.isRequired,
  source: PropTypes.string.isRequired
}

export { menuContentClassName }
export default ReleaseActionsMenuContent
